/**
 * E-Commerce Analytics Dashboard
 * Main App component with navigation and layout
 */

import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Tabs,
  Tab,
  Box,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from '@mui/material';
import {
  ShowChart,
  TrendingUp,
  ShoppingCart,
  Timeline,
  Store,
} from '@mui/icons-material';

// Import feature components
import SalesFunnel from './components/SalesFunnel';
import TopConvertingProducts from './components/TopConvertingProducts';
import AbandonedCarts from './components/AbandonedCarts';
import SessionAnalytics from './components/SessionAnalytics';
import BrandTrends from './components/BrandTrends';

import './App.css';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: '24px' }}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function App() {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        {/* Header */}
        <AppBar position="static" elevation={2}>
          <Toolbar>
            <Store style={{ marginRight: '12px', fontSize: '32px' }} />
            <Typography variant="h5" component="div" style={{ flexGrow: 1 }}>
              E-Commerce Analytics Dashboard
            </Typography>
            <Typography variant="body2" style={{ opacity: 0.8 }}>
              385M+ Events | 27M+ Users
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'white' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            centered
          >
            <Tab icon={<ShowChart />} label="Sales Funnel" iconPosition="start" />
            <Tab icon={<TrendingUp />} label="Top Products" iconPosition="start" />
            <Tab icon={<ShoppingCart />} label="Abandoned Carts" iconPosition="start" />
            <Tab icon={<Timeline />} label="Session Analytics" iconPosition="start" />
            <Tab icon={<Store />} label="Brand Trends" iconPosition="start" />
          </Tabs>
        </Box>

        {/* Main Content */}
        <Container maxWidth="xl" style={{ marginTop: '24px', marginBottom: '48px' }}>
          <TabPanel value={activeTab} index={0}>
            <SalesFunnel />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <TopConvertingProducts />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <AbandonedCarts />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <SessionAnalytics />
          </TabPanel>

          <TabPanel value={activeTab} index={4}>
            <BrandTrends />
          </TabPanel>
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 3,
            px: 2,
            mt: 'auto',
            backgroundColor: '#f5f5f5',
            borderTop: '1px solid #e0e0e0',
          }}
        >
          <Container maxWidth="xl">
            <Typography variant="body2" color="text.secondary" align="center">
              CS 554 Database Systems Final Project | Built with React, FastAPI & PostgreSQL
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center" display="block">
              Dataset: E-Commerce Behavior Data (Oct 2019 - Apr 2020) | {new Date().getFullYear()}
            </Typography>
          </Container>
        </Box>
      </div>
    </ThemeProvider>
  );
}

export default App;
